import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CalendarRange,
  BookOpen,
  GraduationCap,
  LineChart,
  RefreshCw,
  Sparkles,
} from 'lucide-react'
import { useAcademicSession } from '@/contexts/AcademicSessionContext'
import { useAcdmcDashboard } from '@/hooks/useAcdmc'
import { errorMessage } from '@/components/acdmc/AcdmcUi'
import { Badge, Banner, Button, Card, DetailList, EmptyState, Page } from '@/components/ui'
import '@/components/acdmc/acdmc.css'

const toneForStatus = (status: string) => {
  if (status === 'ready') return 'success'
  if (status === 'in_progress') return 'warning'
  if (status === 'unavailable') return 'neutral'
  return 'neutral'
}

const labelForStatus = (status: string) => {
  if (status === 'ready') return 'Ready'
  if (status === 'in_progress') return 'In progress'
  if (status === 'not_started') return 'Not started'
  if (status === 'unavailable') return 'Unavailable'
  return status
}

const AcdmcDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { currentSession } = useAcademicSession()
  const { data, isLoading, isError, error, refetch, isFetching } = useAcdmcDashboard()

  if (isLoading) {
    return (
      <Page>
        <Card>
          <div className="cb-table-state" role="status">
            <span className="cb-spinner" aria-hidden="true" />
            Loading academic dashboard
          </div>
        </Card>
      </Page>
    )
  }

  if (isError || !data) {
    return (
      <Page>
        <Card>
          <EmptyState
            title="Couldn't load the academic dashboard"
            body={errorMessage(error)}
            action={<Button onClick={() => refetch()} icon={RefreshCw}>Try again</Button>}
          />
        </Card>
      </Page>
    )
  }

  const sectionCards = Object.values(data.sections)

  return (
    <Page>
      <div className="cb-stack">
        <Card
          title="Academic dashboard"
          subtitle={`Session ${currentSession || data.session || '—'} · curriculum-first academic management`}
          action={
            <Button size="sm" variant="tertiary" icon={RefreshCw} loading={isFetching} onClick={() => refetch()}>
              Refresh
            </Button>
          }
        >
          <DetailList
            columns={1}
            items={[
              {
                label: 'Foundation',
                value: `Version ${data.workspace.foundationVersion} · initialized ${new Date(data.workspace.initializedAt).toLocaleDateString()}`,
              },
            ]}
          />
        </Card>

        {data.alerts.map((alert) => (
          <Banner key={alert.title} tone={alert.tone === 'info' ? 'info' : 'neutral'} title={alert.title}>
            {alert.body}
          </Banner>
        ))}

        <section className="cb-grid-6-6" aria-label="Readiness by area">
          {sectionCards.map((section) => (
            <Card
              key={section.title}
              title={section.title}
              action={
                section.href ? (
                  <Button size="sm" variant="tertiary" onClick={() => navigate(section.href!)}>
                    Open
                  </Button>
                ) : undefined
              }
            >
              <Badge tone={toneForStatus(section.status)}>{labelForStatus(section.status)}</Badge>
              <p className="cb-muted ac-note">{section.summary}</p>
            </Card>
          ))}
        </section>

        <Card title="Module integrations" subtitle="Read-only contracts — ACDMC does not own these records">
          <DetailList
            items={[
              {
                label: 'Timetable (TMTBL)',
                value: data.integrations.tmtbl.available
                  ? `${String(data.integrations.tmtbl.classMatrixClasses ?? 0)} classes in matrix`
                  : String(data.integrations.tmtbl.message || 'Not available'),
              },
              {
                label: 'Exams (EXMCL)',
                value: data.integrations.exmcl.available
                  ? `${String(data.integrations.exmcl.activeExams ?? 0)} exams · ${String(data.integrations.exmcl.publishedExamSchedules ?? 0)} published schedules`
                  : String(data.integrations.exmcl.message || 'Not available'),
              },
              {
                label: 'Students (STDNT)',
                value: data.integrations.stdnt.available
                  ? `${String(data.integrations.stdnt.activeStudents ?? 0)} active students`
                  : String(data.integrations.stdnt.message || 'Not available'),
              },
              {
                label: 'Attendance (ATTND)',
                value: data.integrations.attnd.available
                  ? 'Module active'
                  : String(data.integrations.attnd.message || 'Not active'),
              },
              {
                label: 'Feedback (FDBCK)',
                value: data.integrations.fdbck.available
                  ? `${String(data.integrations.fdbck.submissionsLast30Days ?? 0)} submissions (30 days)`
                  : String(data.integrations.fdbck.message || 'Not available'),
              },
            ]}
          />
        </Card>

        <Card
          title="Upcoming academic activities"
          subtitle="Live classwork due dates and lesson plans"
          action={
            <Button size="sm" icon={CalendarRange} onClick={() => navigate(data.upcoming.href)}>
              Week overview
            </Button>
          }
        >
          <EmptyState
            compact
            icon={Sparkles}
            title="See the week view for homework, assignments and lesson plans"
            body={data.upcoming.note}
          />
        </Card>

        <Card title="Quick links" flush>
          <div className="ac-list">
            <button type="button" className="ac-list__row" onClick={() => navigate('/acdmc/curriculum')}>
              <BookOpen className="cb-icon" aria-hidden />
              <span>Curriculum and syllabi</span>
            </button>
            <button type="button" className="ac-list__row" onClick={() => navigate('/acdmc/lesson-plan')}>
              <GraduationCap className="cb-icon" aria-hidden />
              <span>Lesson plans</span>
            </button>
            <button type="button" className="ac-list__row" onClick={() => navigate('/acdmc/intelligence')}>
              <LineChart className="cb-icon" aria-hidden />
              <span>Academic intelligence</span>
            </button>
          </div>
        </Card>

        {data.alerts.length === 0 && sectionCards.every((s) => s.status === 'not_started') ? (
          <Banner tone="info" title="Getting started">
            Publish a school syllabus and add lesson plans — metrics on this dashboard stay tied to real records only.
          </Banner>
        ) : null}
      </div>
    </Page>
  )
}

export default AcdmcDashboard
